import React, { useEffect } from 'react';
import { Row, Col } from 'antd';
import { Button, TextField } from 'ui-kit';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { forgotPasswordValidationSchema } from './utils';

interface InitialValues {
  email: string;
}

interface Props {
  initialValues: InitialValues;
  onSubmit: (values: InitialValues) => Promise<void>;
  submitLabel?: string;
  placeholder?: string;
  onEmailChange?: (email: string) => void;
  autoFocus?: boolean;
}

export default function ForgotPasswordForm({
  initialValues,
  onSubmit,
  submitLabel = 'Send reset link',
  placeholder,
  onEmailChange,
  autoFocus = false,
}: Props) {
  const { control, handleSubmit, formState, watch, setFocus } = useForm({
    mode: 'onSubmit',
    defaultValues: initialValues,
    resolver: yupResolver(forgotPasswordValidationSchema),
  });

  const email = watch('email');

  useEffect(() => {
    onEmailChange?.(email);
  }, [email]);

  useEffect(() => {
    if (autoFocus) {
      setFocus('email');
    }
  }, []);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Row gutter={[0, 16]}>
        <Col xs={24}>
          <Controller
            control={control}
            name="email"
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Email"
                type="email"
                placeholder={placeholder}
                fullWidth
                inputRef={ref}
                inputProps={{
                  autoComplete: 'email',
                  autoCapitalize: 'none',
                  autoCorrect: 'off',
                  spellCheck: false,
                }}
                error={Boolean(fieldState.error)}
                errorMessage={fieldState.error?.message}
                {...rest}
              />
            )}
          />
        </Col>
        <Col xs={24}>
          <Button variant="contained" fullWidth loading={formState.isSubmitting} type="submit">
            {submitLabel}
          </Button>
        </Col>
      </Row>
    </form>
  );
}
